import { ArrowLeft, FolderOpen, Plus } from 'lucide-react';
import { useCallback, useState } from 'react';
import { Link } from 'react-router-dom';
import { FolderModal } from '../components/folders/FolderModal';
import { FlowsTable } from '../components/tables/FlowsTable';
import { FoldersTable } from '../components/tables/FoldersTable';
import { useAuth } from '../contexts/AuthContext';
import { useFolderManager } from '../hooks/useFolderManager';
import { useFolder } from '../hooks/useFolders';
import type { Folder } from '../types/api';

/**
 * Main FolderManager component for organizing workflows into folders
 */
export function FolderManager() {
	const { canWrite, canDelete } = useAuth();
	const [selectedFolder, setSelectedFolder] = useState<Folder | null>(null);

	const {
		loading,
		error,
		modal,
		openCreateModal,
		openEditModal,
		closeModal,
		handleCreateFolder,
		handleUpdateFolder,
		handleDeleteFolder,
	} = useFolderManager();

	// Get folder details if a folder is selected
	const { data: folderDetails } = useFolder(selectedFolder?.id || '');

	/**
	 * Handle modal submission
	 */
	const handleModalSubmit = async (name: string) => {
		if (modal.mode === 'create') {
			await handleCreateFolder(name);
		} else {
			await handleUpdateFolder(name);
		}
	};

	/**
	 * Handle folder selection
	 */
	const handleFolderSelect = useCallback((folder: Folder) => {
		setSelectedFolder(folder);
	}, []);

	/**
	 * Go back to folders list
	 */
	const goBackToFolders = useCallback(() => {
		setSelectedFolder(null);
	}, []);

	const isSubmitting =
		loading.action === 'create' || (loading.action?.startsWith('update-') ?? false);

	return (
		<div className="w-full px-4 py-8 sm:px-6 lg:px-8">
			{/* Header */}
			<div className="mb-8">
				{/* Breadcrumb */}
				{selectedFolder && (
					<div className="mb-4 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
						<button
							onClick={goBackToFolders}
							className="hover:text-gray-900 dark:hover:text-gray-200 transition-colors"
						>
							Folders
						</button>
						<span>/</span>
						<span className="text-gray-900 dark:text-gray-200 font-medium">
							{selectedFolder.name}
						</span>
					</div>
				)}

				<div className="flex items-center justify-between">
					<div className="flex items-center gap-4">
						<Link
							to="/workflows"
							className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
							title="Back to workflows"
						>
							<ArrowLeft className="w-5 h-5" />
						</Link>
						<div>
							<h1 className="text-3xl font-bold text-gray-900 dark:text-white">
								{selectedFolder
									? `Folder: ${selectedFolder.name}`
									: 'Folder Management'}
							</h1>
							<p className="mt-1 text-gray-600 dark:text-gray-400">
								{selectedFolder
									? `Viewing workflows in "${selectedFolder.name}" folder`
									: 'Organize your workflows into folders for better management'}
							</p>
						</div>
					</div>
					<div className="flex items-center gap-3">
						{selectedFolder && (
							<button
								onClick={goBackToFolders}
								className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
								title="Back to folders list"
							>
								<FolderOpen className="w-4 h-4 mr-2" />
								Back to Folders
							</button>
						)}
						{!selectedFolder && canWrite() && (
							<button
								onClick={openCreateModal}
								className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors"
								title="Create a new folder for organizing workflows"
							>
								<Plus className="w-4 h-4 mr-2" />
								New Folder
							</button>
						)}
					</div>
				</div>
			</div>

			{/* Error Message */}
			{error && (
				<div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
					<p className="text-red-800 dark:text-red-200">{error}</p>
				</div>
			)}

			{/* Content */}
			{selectedFolder ? (
				// Show workflows in the selected folder
				<FlowsTable
					initialFilters={{ folderId: selectedFolder.id }}
					showSearch={true}
					showFilters={true}
					className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm"
				/>
			) : (
				// Show folders list
				<FoldersTable
					showSearch={true}
					onEdit={canWrite() ? openEditModal : undefined}
					onDelete={canDelete() ? handleDeleteFolder : undefined}
					onFolderSelect={handleFolderSelect}
				/>
			)}

			{/* Create/Edit Modal */}
			<FolderModal
				isOpen={modal.isOpen}
				mode={modal.mode}
				folder={modal.folder}
				isSubmitting={isSubmitting}
				onClose={closeModal}
				onSubmit={handleModalSubmit}
			/>
		</div>
	);
}
